/**
 * BUCHUNGSSTRECKE D4 (und später B3) — der Preisrahmen „48 € bis 72 € pro Person“.
 *
 * Bauplan docs/KONZEPT-VERKAUFSPSYCHOLOGIE.md § 4 Punkt 2 (Bestätigung: „Preisrahmen der Menüs
 * (Preis-Engine)“) und Punkt 3 (Angebotsmail: „Spanne statt Mindestpreis (kein „ab“, Entscheidung
 * 27.08. bleibt)“). Beide Stellen brauchen dieselbe Frage — „zwischen welchen Beträgen je Gast
 * liegt das?“ — deshalb EINE Stelle hier, nicht je Aufrufer eine Kopie.
 *
 * PREIS-INTEGRITÄT: Diese Funktionen RECHNEN keinen Preis aus und ERFINDEN keinen. Sie wählen aus
 * persistierten Beträgen (Integer-Cent, Brutto) den kleinsten und den größten. Welche Spalte eines
 * Pakets je Gast gilt, entscheidet `paketPreisart` (dieselbe Regel wie beim Anwenden eines Pakets).
 *
 * EHRLICHKEIT: Eine Spanne braucht mindestens ZWEI verschiedene Beträge. Ein einzelner Betrag ist
 * keine Spanne, und ein „ab 48 €“ ist ausdrücklich verworfen (27.08.) — dann gibt es `null`, und der
 * Aufrufer lässt den Satz weg, statt einen Rahmen zu behaupten, den es nicht gibt.
 */
import { proPersonCents } from "./pro-person";
import { euro, ortFuer, type AnzeigeSprache } from "./format";
import { paketPreisart, type PaketPreisQuelle } from "./paket-preisart";

export interface PreisSpanne {
  /** Kleinster Betrag in Brutto-Cent (> 0). */
  vonCents: number;
  /** Größter Betrag in Brutto-Cent, immer > `vonCents`. */
  bisCents: number;
}

/**
 * Kleinster und größter Betrag aus einer Liste von Brutto-Cent-Beträgen.
 * Beträge ≤ 0 oder keine ganzen Zahlen zählen nicht (kein Preis hinterlegt ist kein Preis 0).
 * `null`, wenn weniger als zwei VERSCHIEDENE Beträge übrig bleiben.
 */
export function preisSpanne(betraegeCents: readonly number[]): PreisSpanne | null {
  const gueltig = betraegeCents.filter((b) => Number.isInteger(b) && b > 0);
  if (gueltig.length < 2) return null;
  const vonCents = Math.min(...gueltig);
  const bisCents = Math.max(...gueltig);
  return vonCents < bisCents ? { vonCents, bisCents } : null;
}

export interface MenuePaket extends PaketPreisQuelle {
  /** `packages.min_guests` — ab so vielen Gästen buchbar (NULL = keine Grenze). */
  minGuests?: number | null;
  /** `packages.max_guests` — bis so vielen Gästen buchbar (NULL = keine Grenze). */
  maxGuests?: number | null;
}

/**
 * Die Spanne der Menüpreise je Gast aus den Paketen eines Betriebs (Katalog).
 *
 * - Nur die genannten Arten (Vorgabe `menu`): der Satz heißt „Unsere Menüs …“ — ein Buffet für
 *   35 € machte ihn falsch.
 * - Mit Gästezahl nur Pakete, die für diese Gästezahl gelten (`min_guests`/`max_guests`).
 * - Pauschalen (`paketPreisart`) sind kein Menüpreis pro Person und zählen nicht.
 * - Gibt es unter den geltenden Menüs eines OHNE Preis je Gast (Betrag 0, Preis steht in den
 *   Positionen), ist dessen Preis unbekannt und könnte außerhalb liegen → KEINE Spanne (Befund
 *   der Geld-Zweitprüfung zu D4). Lieber kein Satz als ein falscher Rahmen.
 *
 * Archivierte Pakete filtert der Aufrufer (die Datenbank weiß das, diese Funktion nicht).
 */
export function menuePreisSpanne(
  pakete: readonly MenuePaket[],
  opt: { gaeste?: number | null; arten?: readonly string[] } = {},
): PreisSpanne | null {
  const arten = opt.arten ?? ["menu"];
  const gaeste = typeof opt.gaeste === "number" && Number.isInteger(opt.gaeste) && opt.gaeste > 0 ? opt.gaeste : null;
  const betraege: number[] = [];
  for (const p of pakete) {
    if (!arten.includes(p.kind)) continue;
    if (gaeste !== null) {
      if (typeof p.minGuests === "number" && p.minGuests > gaeste) continue;
      if (typeof p.maxGuests === "number" && p.maxGuests > 0 && p.maxGuests < gaeste) continue;
    }
    const art = paketPreisart(p);
    if (art.pauschal) continue;
    if (art.betragCents <= 0) return null;
    betraege.push(art.betragCents);
  }
  return preisSpanne(betraege);
}

/**
 * Ein Betrag für Fließtext: ganze Euro ohne Nachkommastellen („48 €“, „€48“), sonst genau wie
 * `euro()` („48,50 €“). Gerundet wird NICHT — 48,50 € bleibt 48,50 €. Zwischen Zahl und € steht
 * wie bei `euro()` ein geschütztes Leerzeichen (kein Umbruch zwischen Betrag und Währung).
 */
export function euroKurz(cents: number, lang: AnzeigeSprache = "de"): string {
  if (cents % 100 !== 0) return euro(cents, lang);
  const ganz = new Intl.NumberFormat(ortFuer(lang), { maximumFractionDigits: 0 }).format(cents / 100);
  return lang === "en" ? `€${ganz}` : `${ganz}\u00a0€`;
}

/** Kurzform für Betreff/Kasten (B3): „48–72 €“ bzw. „€48–72“. */
export function spanneKurz(s: PreisSpanne, lang: AnzeigeSprache = "de"): string {
  const ganz = s.vonCents % 100 === 0 && s.bisCents % 100 === 0;
  if (!ganz) return `${euroKurz(s.vonCents, lang)} – ${euroKurz(s.bisCents, lang)}`;
  const zahl = (c: number) => new Intl.NumberFormat(ortFuer(lang), { maximumFractionDigits: 0 }).format(c / 100);
  return lang === "en" ? `€${zahl(s.vonCents)}–${zahl(s.bisCents)}` : `${zahl(s.vonCents)}–${zahl(s.bisCents)}\u00a0€`;
}

/** Der ganze Satz: „Unsere Menüs liegen zwischen 48 € und 72 € pro Person, inkl. MwSt.“ (Beträge sind Brutto). */
export function menueSpanneSatz(s: PreisSpanne, lang: AnzeigeSprache = "de"): string {
  if (lang === "en") return `Our menus range from ${euroKurz(s.vonCents, "en")} to ${euroKurz(s.bisCents, "en")} per person incl. VAT.`;
  if (lang === "it") return `I nostri menù vanno da ${euroKurz(s.vonCents, "it")} a ${euroKurz(s.bisCents, "it")} a persona, IVA inclusa.`;
  if (lang === "fr") return `Nos menus vont de ${euroKurz(s.vonCents, "fr")} à ${euroKurz(s.bisCents, "fr")} par personne, TVA incluse.`;
  return `Unsere Menüs liegen zwischen ${euroKurz(s.vonCents)} und ${euroKurz(s.bisCents)} pro Person, inkl. MwSt.`;
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * BUCHUNGSSTRECKE B3 — der Preisrahmen der ANGEBOTSMAIL, aus den Varianten dieses Angebots.
 *
 * Anders als D4 (Katalog-Pakete) stammt der Rahmen hier aus den versendeten Varianten selbst:
 * `amount_total_cents` (persistierter Engine-Endbetrag, Brutto) und `guest_count` je Variante.
 * Der Betrag pro Person ist GENAU die Zahl, die `computeBreakdown().perPersonCents` und die
 * Angebotsseite je Variante zeigen (`Math.round(Summe / Gäste)`) — dieselbe Formel, jetzt an EINER
 * Stelle (`proPersonCents`), die `computeBreakdown` selbst benutzt.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */


export interface VarianteBetrag {
  /** `offer_options.amount_total_cents` (bzw. derselbe Wert aus dem Versand-Snapshot). */
  amountTotalCents: number;
  /** `offer_options.guest_count` — die Zahl, auf die der Endbetrag gerechnet ist (§5.19). */
  guestCount: number;
}

export interface VariantenRahmen {
  /** `pro_person`, wenn JEDE Variante eine Gästezahl trägt; sonst ehrlich der Gesamtbetrag. */
  art: "pro_person" | "gesamt";
  vonCents: number;
  /** ≥ `vonCents`; gleich, wenn es nur eine Variante oder nur gleiche Beträge gibt. */
  bisCents: number;
}

/**
 * Kleinster und größter Betrag der Varianten — pro Person, wenn für alle berechenbar.
 * Hat auch nur EINE Variante keinen gültigen Endbetrag (≤ 0, keine ganze Zahl), gibt es keinen
 * Rahmen (`null`): „48–72 €“ über drei Menüs, von denen eines 0 € kostet, wäre unwahr
 * (Geld-Zweitprüfung #1509, Befund 1). Ohne Varianten ebenfalls `null`.
 * Fehlt auch nur EINER Variante die Gästezahl, wäre ein Rahmen pro Person unvollständig (ihr Preis
 * könnte außerhalb liegen) — dann der Rahmen der Gesamtbeträge statt eines falschen pro Person.
 */
export function variantenRahmen(varianten: readonly VarianteBetrag[]): VariantenRahmen | null {
  if (varianten.length === 0) return null;
  if (!varianten.every((v) => Number.isInteger(v.amountTotalCents) && v.amountTotalCents > 0)) return null;
  const gueltig = varianten;
  const proPerson = gueltig.every((v) => Number.isInteger(v.guestCount) && v.guestCount > 0);
  const betraege = gueltig.map((v) => (proPerson ? (proPersonCents(v.amountTotalCents, v.guestCount) ?? 0) : v.amountTotalCents));
  return { art: proPerson ? "pro_person" : "gesamt", vonCents: Math.min(...betraege), bisCents: Math.max(...betraege) };
}

/**
 * Der Rahmen als Text für Mail-Kasten und Klartext: „48–72 € pro Person, inkl. MwSt.“, bei einem
 * Betrag „48 € pro Person, inkl. MwSt.“; ohne Gästezahl „1.920–2.880 € gesamt, inkl. MwSt.“.
 * Kein „ab“ (Entscheidung 27.08.2026) — es steht immer der ganze Rahmen da.
 */
export function variantenRahmenText(r: VariantenRahmen, lang: AnzeigeSprache = "de"): string {
  const betrag = r.vonCents === r.bisCents ? euroKurz(r.vonCents, lang) : spanneKurz({ vonCents: r.vonCents, bisCents: r.bisCents }, lang);
  if (lang === "en") return `${betrag} ${r.art === "pro_person" ? "per person" : "in total"}, incl. VAT`;
  if (lang === "it") return `${betrag} ${r.art === "pro_person" ? "a persona" : "in totale"}, IVA inclusa`;
  if (lang === "fr") return `${betrag} ${r.art === "pro_person" ? "par personne" : "au total"}, TVA incluse`;
  return `${betrag} ${r.art === "pro_person" ? "pro Person" : "gesamt"}, inkl. MwSt.`;
}
